// CountryDropdown — DMC selector in the header (US-877 · R-3/R-4/R-5/R-8).
//
// A second entry point to the SAME selection as clicking the map: choosing a
// country calls selectCountry(iso3) (centers + selects it); a map click flows
// back as this control's value, since both write the one `selectedCountry`. One
// source of truth (SelectionContext) — no parallel state, no data fetch (R-7).
// Options come from dmcConfig, which is derived from SELECTABLE_ISO3, so China
// can never appear (R-6). Styled for the navy header: light text on a translucent
// field, with the gold accent on focus (the One Accent Rule). Hidden on phones to
// keep the bar uncrowded — the map click and the panel remain available there.
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import useSelection from "../../hooks/useSelection";
import { DMC_COUNTRIES, DMC_BY_ISO3 } from "../../config/dmcConfig";

const LIGHT = "rgba(255,255,255,0.72)";

const CountryDropdown = () => {
  const { selection, selectCountry, clearSelection } = useSelection();
  // The active option mirrors whatever is selected (map click or dropdown). Any
  // drill level still carries the country iso3, so the country stays shown.
  const value = (selection?.iso3 && DMC_BY_ISO3[selection.iso3]) || null;

  return (
    <Autocomplete
      size="small"
      options={DMC_COUNTRIES}
      value={value}
      onChange={(_event, option) =>
        option ? selectCountry(option.iso3) : clearSelection()
      }
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, val) => option.iso3 === val.iso3}
      autoHighlight
      openOnFocus
      blurOnSelect
      sx={{
        display: { xs: "none", md: "block" },
        width: 248,
        flexShrink: 0,
        "& .MuiOutlinedInput-root": {
          color: "primary.contrastText",
          bgcolor: "rgba(255,255,255,0.08)",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(255,255,255,0.28)",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(255,255,255,0.5)",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "secondary.main",
            borderWidth: 2,
          },
        },
        "& .MuiInputBase-input::placeholder": { color: LIGHT, opacity: 1 },
        "& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator": {
          color: LIGHT,
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Select a country"
          inputProps={{ ...params.inputProps, "aria-label": "Select a country" }}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <PublicOutlinedIcon
                sx={{ fontSize: 18, ml: 0.25, mr: 0.5, color: LIGHT }}
              />
            ),
          }}
        />
      )}
    />
  );
};

export default CountryDropdown;
